<template>
	<v-card v-if="user" class="profile-page pa-7 page" style="border-radius: 10px; max-width: 600px;" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-card-text>
				<ButtonBack />
	
				<div class="user__brief-info__title d-flex my-10">
					<Avatar :avatar="avatarPath" size="140px" border-radius="square" class="user__avatar mr-10" />
	
					<div class="user__brief-description">
						<h1 class="user__brief-description__username mb-3">{{ userName }}</h1>
	
						<div class="d-flex flex-column">
							<span v-if="companyName" class="user__brief-description__company mb-1">
								{{ companyName }}
							</span>
	
							<span v-if="heldPosition" class="user__brief-description__held-position mb-1">
								{{ heldPosition }}
							</span>
	
							<span v-if="location" class="user__brief-description__location mb-1 d-flex justify-space-between">
								<Icon 
									type="pin"
									color="var(--location-color)"
									:size="16"
									:pr="4"
								/>
								<span>{{ location }}</span>
							</span>
						</div>
					</div>
				</div>
	
				<div v-for="(field, index) in allowFields" :key="field.field" :class="fieldClass(allowFields, index)">
					<h3 v-if="!allowFields[index - 1] || allowFields[index - 1].section !== field.section" class="mb-3">
						{{ field.section }}
					</h3>
					<div v-if="field.type === 'email'">
						{{ field.value }}
					</div>
					<div 
						v-else-if="field.section === 'Network' && (index === 0 || allowFields[index - 1].section !== 'Network')" 
						class="user-profile-page__network pt-4"
					>
						<a 
							v-for="item in networkFields" 
							:key="item.field" 
							:href="item.value" 
							target="_blank"
						>
							<Icon 
								:size="28"
								:type="networkFieldType(item.type)"
							/>
						</a>
					</div>
					<div v-else-if="field.type === 'phone'" class="py-3 d-flex">
						<Icon 
							type="phone"
							color="#a0a0a0"
							:pr="12"
							:size="22"
						/>
						<a @click="copyPhone(field.value)" :href="null" style="text-decoration: underline; color: var(--ceil-detail-text-color)">
							{{ field.value }}
						</a>
					</div>
					<v-card-text v-else-if="field.type === 'html'" v-html="field.value" />
					<div v-else-if="field.section !== 'Network'" class="mb-4">
						<span class="font-weight-bold mr-2">
							{{ field.title }}:
						</span>
						<span>
							{{ field.value }}
						</span>
					</div>
				</div>
	
				<v-skeleton-loader width="600" v-bind="attrs" type="card" v-if="false">
				</v-skeleton-loader>
			</v-card-text>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import Avatar from '@/components/Avatar';
import PageBlocked from '@/components/PageBlocked'
import ButtonBack from '@/components/ButtonBack'
import Block from '@/mixins/Block.js'
import twitterLogo from '@/assets/images/twitterX-logo.svg'

import _ from 'lodash'

export default {
	name: 'ProfilePage',
	mixins: [Block],
	components: {
		PageBlocked,
		Avatar,
		ButtonBack
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('settings', ['user', 'backUser', 'appCore']),
		...mapGetters('app', ['isShowAlert', 'alertMessage']),

		allowFields() {
			return this.profile.private.filter(field => field.value)
		},
		networkFields() {
			return this.profile.private.filter(field => field.section === 'Network' && field.value)
		},
		profileFieldsType() {
			if (!this.user) {
				return 'public';
			}

			const {view_profile_link: viewProfileLink} = this.user

			return this.getTypeFieldsFromUrl(viewProfileLink)
		},
		isVerticalTabs() {
			return this.$vuetify.breakpoint.mdAndUp
		},

		// short profile
		location() {
			const {location_profile_row: location = {}} = this.profile
			return location;
		},
		userName() {
			const {visible_username: name} = this.profile
			return name;
		},
		avatarPath() {
			const {user_logo: userLogo} = this.profile
			return userLogo;
		},
		heldPosition() {
			const {third_profile_row: heldPosition} = this.profile
			return heldPosition;
		},
		companyName() {
			const {second_profile_row: company} = this.profile
			return company;
		},
	},
	methods: {
		...mapActions('app', ['showAlert', 'hideAlert']),
		...mapActions('settings', ['getUserSettings']),

		onTabSwitch() {
			this.successMessage = false
			this.errorMessage = false
		},
		normalizeSettings(item) {
			const { setting, value } = item

			let settings = {}
			settings[setting.name] = value !== '0'

			return settings
		},
		formatUrl(url) {
			if (!url) {
				return '';
			}

			if (!url.startsWith('http://') && !url.startsWith('https://')) {
				if (url.startsWith('www.')) {
					url = 'https://' + url;
				} else {
					url = 'https://www.' + url;
				}
			}
			return url;
		},
		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '');

				this.$copyText( phone ).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)

					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
		dateCreated(originalDate) {
			return new Date(originalDate * 1000).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			})
		},
		getTypeFieldsFromUrl(url) {
			const parts = url.split('/');
			return parts[parts.length - 1];
		},
		fieldClass(allowFields, index) {
			if (index === 0 || allowFields[index].section !== allowFields[index - 1].section) {
				return 'mt-8'
			} else if (!allowFields[index + 1] || allowFields[index + 1].section !== allowFields[index].section) {
				return 'mb-8'
			} else {
				return ''
			}
		},
		networkFieldType(type) {
			if (type === 'url') {
				return 'link'
			} else {
				return type
			}
		}
	},
	data() {
		return {
			searchIcon,
			twitterLogo,
			successMessage: false,
			errorMessage: false,
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			socialFieldNames: [
				'user_linkedin',
				'user_facebook',
				'user_twitter',
				'user_custom_url'
			],
			phoneFieldNames: [
				'user_work_phone',
				'user_cell_phone'
			],
			pageBlockedName: 'profile'
		}
	},
	created() {
		if (!this.user) {
			this.getUserSettings()
		}
	},
}
</script>

<style lang="scss">
.v-list-item {
	padding: 0;

	.v-icon {
		margin-right: 12px;
	}
}
.user-profile-page {
	font-family: Montserrat,sans-serif;
	margin-bottom: 30px;
	border-radius: 10px !important;
	max-width: 600px;
	width: 100%;

	@media (max-width: 768px) {
		margin-bottom: 12px;
	}

	&__network {
		display: flex;
		justify-content: center;
		column-gap: 24px;
	}

	&__user-info {
		@media (max-width: 480px) {
			flex-direction: column;

			&-avatar-wrapper {
				display: flex;
				justify-content: center;
				margin-bottom: 12px;
				padding-left: 40px;
			}
		}
		&-text {
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}
	}

	&__network-link {
		word-break: break-word;
	}
}
.user {
	&__home-address,
	&__work-address {
		background: rgb(251, 251, 251);
		border-radius: 12px;
		border: 1px solid rgb(226, 226, 226);
	}
	&__brief-description {
		&__location {
			margin-left: -6px;
		}
	}
	&__phone-numbers > ul,
	&__networks > ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	&__networks-link {
		word-break: break-word;
		color: var(--ceil-detail-text-color) !important;
	}
}
</style>
